import {
  Briefcase,
  Building2,
  Store,
  Users,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type { BusinessType, KycStatus } from "@/types/kyc";

export const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export function formatDate(iso?: string | null): string {
  if (!iso) return "—";
  if (typeof iso === "string" && (iso.includes(" ") || iso.includes("/")) && isNaN(Date.parse(iso))) {
    return iso;
  }
  const d = new Date(iso);
  if (isNaN(d.getTime())) return String(iso);
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

export function formatINR(n?: number | string | null): string {
  const num = Number(n);
  const safeNum = Number.isFinite(num) ? num : 0;
  return `\u20B9${safeNum.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export const KYC_STATUS_META: Record<KycStatus, { label: string; text: string; bg: string }> = {
  NOT_STARTED: { label: "Not Started", text: "text-slate-600", bg: "bg-badgeSlate-bg" },
  DRAFT: { label: "Draft", text: "text-amber-700", bg: "bg-warning-bg" },
  SUBMITTED: { label: "Submitted", text: "text-sky-700", bg: "bg-info-bg" },
  UNDER_REVIEW: { label: "Under Review", text: "text-amber-700", bg: "bg-warning-bg" },
  APPROVED: { label: "Approved", text: "text-emerald-700", bg: "bg-success-bg" },
  REJECTED: { label: "Rejected", text: "text-red-700", bg: "bg-danger-bg" },
};

export interface BusinessTypeOption {
  value: BusinessType;
  sub: string;
  icon: LucideIcon;
}

export const BUSINESS_TYPES: BusinessTypeOption[] = [
  { value: "Proprietorship", sub: "Single owner", icon: Store },
  { value: "Partnership", sub: "Two or more partners", icon: Users },
  { value: "Private Limited", sub: "Registered company", icon: Building2 },
  { value: "LLP", sub: "Limited liability partnership", icon: Briefcase },
];

export interface RequiredDoc {
  key: string;
  label: string;
  helper: string;
}

export const REQUIRED_DOCS: Record<BusinessType, RequiredDoc[]> = {
  Proprietorship: [
    { key: "gstOrUdyam", label: "GST Certificate or Udyam Registration", helper: "Upload either your GST registration certificate or your Udyam registration certificate" },
    { key: "businessPan", label: "Business PAN Card", helper: "Clear image of the PAN card issued in the business name" },
  ],
  Partnership: [
    { key: "partnershipDeed", label: "Partnership Deed", helper: "A signed and registered copy of the partnership deed" },
    { key: "gstCertificate", label: "GST Certificate", helper: "GST registration certificate of the partnership firm" },
    { key: "businessPan", label: "Business PAN Card", helper: "PAN card issued in the partnership firm's name" },
  ],
  "Private Limited": [
    { key: "certificateOfIncorporation", label: "Certificate of Incorporation", helper: "CIN certificate issued by the Ministry of Corporate Affairs" },
    { key: "gstCertificate", label: "GST Certificate", helper: "GST registration certificate of the company" },
    { key: "companyPan", label: "Company PAN Card", helper: "PAN card issued in the company's name" },
  ],
  LLP: [
    { key: "certificateOfIncorporation", label: "Certificate of Incorporation", helper: "LLP incorporation certificate issued by the Ministry of Corporate Affairs" },
    { key: "gstCertificate", label: "GST Certificate", helper: "GST registration certificate of the LLP" },
    { key: "companyPan", label: "Company PAN Card", helper: "PAN card issued in the LLP's name" },
  ],
};

export const PERSONAL_DOCS: RequiredDoc[] = [
  { key: "aadhaarFront", label: "Aadhaar Card — Front", helper: "Front side of the Aadhaar card" },
  { key: "aadhaarBack", label: "Aadhaar Card — Back", helper: "Back side of the Aadhaar card" },
  { key: "panCard", label: "PAN Card", helper: "PAN card of the owner or authorised signatory" },
  { key: "selfie", label: "Selfie", helper: "A clear photograph of your face in good lighting, holding your PAN card" },
];

export const NATURE_OPTIONS = [
  "Personal Loans",
  "Vehicle Finance",
  "Gold Loan",
  "Business Loan",
  "Consumer Durable Finance",
  "Microfinance",
  "Other",
].map((n) => ({ value: n, label: n }));

export const YEARS = Array.from({ length: 47 }, (_, i) => 2026 - i).map((y) => ({
  value: String(y),
  label: String(y),
}));

export const STATES = [
  "Andhra Pradesh",
  "Assam",
  "Bihar",
  "Chhattisgarh",
  "Delhi",
  "Goa",
  "Gujarat",
  "Haryana",
  "Himachal Pradesh",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Madhya Pradesh",
  "Maharashtra",
  "Odisha",
  "Punjab",
  "Rajasthan",
  "Tamil Nadu",
  "Telangana",
  "Uttar Pradesh",
  "Uttarakhand",
  "West Bengal",
].map((s) => ({ value: s, label: s }));

export const BANK_OPTIONS = [
  "HDFC Bank",
  "ICICI Bank",
  "State Bank of India",
  "Axis Bank",
  "Kotak Mahindra Bank",
  "Punjab National Bank",
  "Bank of Baroda",
  "Canara Bank",
  "Union Bank of India",
  "IndusInd Bank",
  "Yes Bank",
  "IDFC First Bank",
].map((b) => ({ value: b, label: b }));

const BANK_BRANCHES: Record<string, string> = {
  "HDFC Bank": "HDFC Bank — Connaught Place Branch, New Delhi",
  "ICICI Bank": "ICICI Bank — Bandra Kurla Complex Branch, Mumbai",
  "State Bank of India": "State Bank of India — Rohini Sector 24 Branch, Delhi",
  "Axis Bank": "Axis Bank — Koramangala Branch, Bengaluru",
  "Kotak Mahindra Bank": "Kotak Mahindra Bank — Fort Branch, Mumbai",
  "Punjab National Bank": "Punjab National Bank — Karol Bagh Branch, New Delhi",
  "Bank of Baroda": "Bank of Baroda — Alipore Branch, Kolkata",
  "Canara Bank": "Canara Bank — MG Road Branch, Bengaluru",
  "Union Bank of India": "Union Bank of India — Gariahat Branch, Kolkata",
  "IndusInd Bank": "IndusInd Bank — Andheri West Branch, Mumbai",
  "Yes Bank": "Yes Bank — Worli Branch, Mumbai",
  "IDFC First Bank": "IDFC First Bank — Powai Branch, Mumbai",
};

export const IFSC_RE = /^[A-Z]{4}0[A-Z0-9]{6}$/;

export function resolveBank(bankName: string, ifsc: string): string | null {
  if (!IFSC_RE.test(ifsc)) return null;
  if (bankName) return BANK_BRANCHES[bankName] ?? bankName;
  return null;
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}